import { useState, useMemo } from "react";
import { EventComplianceFlag } from "../../lib/api/w3-wizard";

export interface TimelineEvent {
  event_type: string;
  description: string;
  depth_top_ft?: number;
  depth_bottom_ft?: number;
  sacks?: number;
}

export interface TimelineDay {
  day_number: number;
  work_date: string;
  narrative: string;
  events: TimelineEvent[];
}

interface OperationsTimelineProps {
  days: TimelineDay[];
  editable?: boolean;
  onDaysChange?: (days: TimelineDay[]) => void;
  complianceFlags?: EventComplianceFlag[];
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  set_cement_plug: "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
  set_surface_plug: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  set_bridge_plug: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  pump_cement: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  squeeze: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  pressure_test: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  woc: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  circulate: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  pull_tubing: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  cut_casing: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  tag_toc: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  default: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

function formatEventType(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function EventBadge({ type }: { type: string }) {
  const colorClass = EVENT_TYPE_COLORS[type] ?? EVENT_TYPE_COLORS.default;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {formatEventType(type)}
    </span>
  );
}

export default function OperationsTimeline({ days, editable = false, onDaysChange, complianceFlags }: OperationsTimelineProps) {
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(new Set());
  const [pendingRemove, setPendingRemove] = useState<
    | { type: "event"; dayNumber: number; eventIndex: number }
    | { type: "day"; dayNumber: number }
    | null
  >(null);

  const flagMap = useMemo(() => {
    const map = new Map<string, EventComplianceFlag[]>();
    for (const flag of complianceFlags ?? []) {
      const key = `${flag.day_number}-${flag.event_index}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(flag);
    }
    return map;
  }, [complianceFlags]);

  const toggleCollapse = (dayNumber: number) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) {
        next.delete(dayNumber);
      } else {
        next.add(dayNumber);
      }
      return next;
    });
  };

  const updateNarrative = (dayNumber: number, narrative: string) => {
    if (!onDaysChange) return;
    onDaysChange(days.map((d) => (d.day_number === dayNumber ? { ...d, narrative } : d)));
  };

  const removeEvent = (dayNumber: number, eventIndex: number) => {
    if (!onDaysChange) return;
    onDaysChange(
      days.map((d) =>
        d.day_number === dayNumber
          ? { ...d, events: d.events.filter((_, i) => i !== eventIndex) }
          : d
      )
    );
  };

  const addDay = () => {
    if (!onDaysChange) return;
    const maxDay = days.length > 0 ? Math.max(...days.map((d) => d.day_number)) : 0;
    const newDay: TimelineDay = {
      day_number: maxDay + 1,
      work_date: "",
      narrative: "",
      events: [],
    };
    onDaysChange([...days, newDay]);
  };

  const removeDay = (dayNumber: number) => {
    if (!onDaysChange) return;
    onDaysChange(
      days
        .filter((d) => d.day_number !== dayNumber)
        .map((d, i) => ({ ...d, day_number: i + 1 }))
    );
  };

  const updateWorkDate = (dayNumber: number, work_date: string) => {
    if (!onDaysChange) return;
    onDaysChange(days.map((d) => (d.day_number === dayNumber ? { ...d, work_date } : d)));
  };

  if (days.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
        <p>No operations data available.</p>
        {editable && (
          <button
            onClick={addDay}
            className="mt-3 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            Add Day
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-white/[0.08]" />

        <div className="space-y-4">
          {days.map((day) => {
            const isCollapsed = collapsedDays.has(day.day_number);
            return (
              <div key={day.day_number} className="relative flex gap-4">
                {/* Day circle */}
                <div className="relative flex-shrink-0 w-10 h-10 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center z-10">
                  {day.day_number}
                </div>

                {/* Card */}
                <div className="flex-1 rounded-xl border border-gray-200 dark:border-white/[0.05] bg-white dark:bg-white/[0.03] mb-1">
                  {/* Card header */}
                  <button
                    onClick={() => toggleCollapse(day.day_number)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                          Day {day.day_number}
                          {day.work_date && (
                            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                              — {day.work_date}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {day.events.length} event{day.events.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Card body */}
                  {!isCollapsed && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-white/[0.05] pt-3">
                      {/* Date field (editable) */}
                      {editable && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Work Date
                          </label>
                          <input
                            type="date"
                            value={day.work_date}
                            onChange={(e) => updateWorkDate(day.day_number, e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                      )}

                      {/* Events */}
                      {day.events.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Events</p>
                          {day.events.map((event, eIdx) => (
                            <div
                              key={eIdx}
                              className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.02]"
                            >
                              <div className="flex-1 min-w-0 space-y-1">
                                <EventBadge type={event.event_type} />
                                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {event.description}
                                </p>
                                {(event.depth_top_ft !== undefined || event.sacks !== undefined) && (
                                  <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                                    {event.depth_top_ft !== undefined && (
                                      <span>
                                        Depth: {event.depth_top_ft.toLocaleString()}
                                        {event.depth_bottom_ft !== undefined
                                          ? `–${event.depth_bottom_ft.toLocaleString()}`
                                          : ""}{" "}
                                        ft
                                      </span>
                                    )}
                                    {event.sacks !== undefined && (
                                      <span>{event.sacks} sacks</span>
                                    )}
                                  </div>
                                )}
                                {(() => {
                                  const eventFlags = flagMap.get(`${day.day_number}-${eIdx}`);
                                  if (!eventFlags?.length) return null;
                                  return (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {eventFlags.map((flag, fi) => (
                                        <span
                                          key={fi}
                                          title={`${flag.detail}${flag.citation ? `\nCitation: ${flag.citation}` : ''}`}
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${
                                            flag.severity === 'violation'
                                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                              : flag.severity === 'warning'
                                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                          }`}
                                        >
                                          {flag.severity === 'violation' ? (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          ) : flag.severity === 'warning' ? (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                                            </svg>
                                          ) : (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
                                            </svg>
                                          )}
                                          {flag.rule_label}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                              {editable && (
                                pendingRemove?.type === "event" &&
                                pendingRemove.dayNumber === day.day_number &&
                                pendingRemove.eventIndex === eIdx ? (
                                  <span className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                                    <button
                                      onClick={() => { removeEvent(day.day_number, eIdx); setPendingRemove(null); }}
                                      className="text-xs text-red-600 dark:text-red-400 font-medium hover:underline"
                                    >
                                      Remove
                                    </button>
                                    <span className="text-gray-300 dark:text-gray-600">|</span>
                                    <button
                                      onClick={() => setPendingRemove(null)}
                                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setPendingRemove({ type: "event", dayNumber: day.day_number, eventIndex: eIdx })}
                                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 mt-0.5"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Narrative */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                          Daily Narrative
                        </p>
                        {editable ? (
                          <textarea
                            value={day.narrative}
                            onChange={(e) => updateNarrative(day.day_number, e.target.value)}
                            rows={3}
                            placeholder="Describe daily operations..."
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                          />
                        ) : (
                          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            {day.narrative || <span className="text-gray-400 dark:text-gray-500 italic">No narrative provided.</span>}
                          </p>
                        )}
                      </div>

                      {/* Remove day button */}
                      {editable && (
                        pendingRemove?.type === "day" && pendingRemove.dayNumber === day.day_number ? (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-600 dark:text-gray-400">Remove Day {day.day_number}?</span>
                            <button
                              onClick={() => { removeDay(day.day_number); setPendingRemove(null); }}
                              className="text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setPendingRemove(null)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setPendingRemove({ type: "day", dayNumber: day.day_number })}
                            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Remove Day
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Day */}
      {editable && (
        <div className="pl-14 pt-2">
          <button
            onClick={addDay}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-600 dark:text-brand-400 border border-brand-300 dark:border-brand-700 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Day
          </button>
        </div>
      )}
    </div>
  );
}
